import { Navigation } from '@src/components/layout/Navigation';
import { config, isAuthConfigured } from '@src/config';
import { Cost } from '@src/pages/Cost';
import { Coverage } from '@src/pages/Coverage';
import { Enrichments } from '@src/pages/Enrichments';
import { Latency } from '@src/pages/Latency';
import { Login } from '@src/pages/Login';
import { Overview } from '@src/pages/Overview';
import { RunDetail } from '@src/pages/RunDetail';
import { Runs } from '@src/pages/Runs';
import { resources } from '@src/resources';
import { setAuthToken } from '@src/services/apiClient';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WebStorageStateStore } from 'oidc-client-ts';
import { useMemo } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { HashRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

/** Build OIDC config at runtime so it uses loaded config (from /config.json), not module-load defaults. */
function getOidcConfig() {
  return {
    authority: config.auth.authority,
    client_id: config.auth.clientId,
    redirect_uri: config.auth.redirectUri,
    post_logout_redirect_uri: config.auth.postLogoutRedirectUri,
    scope: config.auth.scopes,
    response_type: 'code',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

/** Shown when /config.json has not provided OIDC (e.g. local dev without auth). */
function AuthNotConfigured() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-10 text-center text-muted-foreground shadow-xl">
        <p className="font-medium">{resources.login.title}</p>
        <p className="mt-2 text-sm">
          Authentication is not configured for this environment. Provide OIDC settings in runtime config (e.g.
          /config.json) to sign in.
        </p>
      </div>
    </div>
  );
}

/**
 * Layout route for every authenticated screen: guards on auth state once, then
 * renders the persistent Navigation shell with the matched child route in its
 * <Outlet/>. Nested under a single <Route> (see AppRoutes) so the sidebar
 * mounts once and survives navigation between screens, instead of remounting
 * per-route.
 */
function ProtectedLayout() {
  const auth = useAuth();
  const location = useLocation();

  // Set token synchronously during render so the first data fetches in child
  // components see it. If we only set in useEffect, those useEffects run in the
  // same tick and getAuthToken() is still undefined.
  //
  // id_token, NOT access_token: the /ops/* Cognito authorizer (EnrichApiGateway,
  // aws-cdk-lib's CognitoUserPoolsAuthorizer) validates ID tokens -- an access
  // token has no `aud` claim in the shape it expects, so it 401s. That 401 (like
  // every API-Gateway-native auth rejection) carries no CORS headers, so the
  // browser reports it as a bare "Failed to fetch" instead of a readable 401 --
  // confirmed live by sending each token type by hand against the deployed API.
  setAuthToken(auth.user?.id_token);

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex animate-pulse flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary"></div>
          <div className="h-4 w-32 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Navigation>
      <Outlet />
    </Navigation>
  );
}

function AppRoutes() {
  const auth = useAuth();

  return (
    <Routes>
      <Route path="/login" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <Login />} />

      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Overview />} />
        <Route path="/runs" element={<Runs />} />
        <Route path="/runs/detail" element={<RunDetail />} />
        <Route path="/enrichments" element={<Enrichments />} />
        <Route path="/cost" element={<Cost />} />
        <Route path="/latency" element={<Latency />} />
        <Route path="/coverage" element={<Coverage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const oidcConfig = useMemo(() => getOidcConfig(), []);

  if (!isAuthConfigured()) {
    return <AuthNotConfigured />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider {...oidcConfig}>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
